/**
 * O escritório que conduz o processo, na abertura pelo banco (E143.F2, GUS-140, ADR-0097).
 *
 * O banco abre o caso no próprio ambiente e indica o escritório entre os vinculados a ele
 * (ADR-0088 §2.8): com um só vínculo, ele já vem escolhido e aparece só como informação; com
 * mais de um, a escolha é obrigatória. O servidor confere de novo — a lista daqui é conveniência.
 */
import { useEffect, useState } from 'react';
import type { AxiosInstance } from 'axios';

import { Field, Select } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import {
  listarEscritoriosVinculados,
  type EscritorioVinculadoData,
} from '@/lib/portal/escritoriosVinculados';

import { CartaoDaAbertura } from './PecasDaAbertura';

const t = ptBR.abertura.escritorio;

export interface EscritorioDoBancoProps {
  client: AxiosInstance;
  valor: string | null;
  onChange: (escritorioId: string | null) => void;
  invalido: boolean;
  disabled?: boolean;
}

type Estado =
  | { tipo: 'carregando' }
  | { tipo: 'erro' }
  | { tipo: 'pronto'; lista: EscritorioVinculadoData[] };

export function EscritorioDoBanco({
  client,
  valor,
  onChange,
  invalido,
  disabled = false,
}: EscritorioDoBancoProps): JSX.Element {
  const [estado, setEstado] = useState<Estado>({ tipo: 'carregando' });

  useEffect(() => {
    let vivo = true;
    listarEscritoriosVinculados(client)
      .then((lista) => {
        if (!vivo) return;
        setEstado({ tipo: 'pronto', lista });
        if (lista.length === 1 && lista[0] !== undefined) onChange(lista[0].id);
      })
      .catch(() => {
        if (vivo) setEstado({ tipo: 'erro' });
      });
    return () => {
      vivo = false;
    };
    // `onChange` é estável o bastante: a lista é lida uma vez por abertura.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  return (
    <CartaoDaAbertura titulo={t.titulo} testId="abertura-escritorio">
      <Conteudo
        estado={estado}
        valor={valor}
        onChange={onChange}
        invalido={invalido}
        disabled={disabled}
      />
    </CartaoDaAbertura>
  );
}

function Conteudo({
  estado,
  valor,
  onChange,
  invalido,
  disabled,
}: {
  estado: Estado;
  valor: string | null;
  onChange: (escritorioId: string | null) => void;
  invalido: boolean;
  disabled: boolean;
}): JSX.Element {
  if (estado.tipo === 'carregando') {
    return <p className="text-body-sm text-fg-muted">{t.carregando}</p>;
  }
  if (estado.tipo === 'erro') {
    return (
      <p role="alert" className="text-body-sm text-danger" data-testid="abertura-escritorio-erro">
        {t.erro}
      </p>
    );
  }
  if (estado.lista.length === 0) {
    return (
      <p className="text-body-sm text-fg" data-testid="abertura-escritorio-nenhum">
        {t.nenhum}
      </p>
    );
  }
  if (estado.lista.length === 1 && estado.lista[0] !== undefined) {
    return (
      <p className="text-body-sm text-fg" data-testid="abertura-escritorio-unico">
        {estado.lista[0].nome}
        <span className="mt-sc-1 block text-caption text-fg-muted">{t.ajuda}</span>
      </p>
    );
  }
  return (
    <Field
      label={t.rotulo}
      description={t.ajuda}
      error={
        invalido ? (
          <span data-testid="abertura-escritorio-obrigatorio">{t.obrigatorio}</span>
        ) : undefined
      }
      required
    >
      <Select
        data-testid="abertura-escritorio-select"
        value={valor ?? ''}
        disabled={disabled}
        onChange={(evt) => onChange(evt.target.value === '' ? null : evt.target.value)}
      >
        <option value="">{t.escolha}</option>
        {estado.lista.map((e) => (
          <option key={e.id} value={e.id}>
            {e.nome}
          </option>
        ))}
      </Select>
    </Field>
  );
}
